import React from "react";
import { Mail, Phone, MapPin, Send, Loader2, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactInfo = [{
  icon: Mail,
  label: "EMAIL",
  value: "segreteria@studiolegale.it",
  link: "mailto:segreteria@studiolegale.it",
  description: "Per richieste di consulenza e appuntamenti"
}, {
  icon: Phone,
  label: "TELEFONO",
  value: "+39 02 123 4567",
  link: "tel:+39021234567",
  description: "Disponibile Lun-Ven, 09:00 - 18:00"
}, {
  icon: MapPin,
  label: "SEDE",
  value: "Milano, Italia",
  link: "/FindUsPage",
  description: "Via Montenapoleone, 12",
  newTab: false
}];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="4217" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 bg-accent/20 border border-primary/20 rounded-none animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mb-6">
          <Send className="size-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground mb-2">Messaggio Inviato</h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-8 font-serif italic">{successMessage}</p>
        <Button onClick={() => window.location.reload()} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none font-mono text-xs">
          Invia un altro messaggio
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Nome Completo</FormLabel>
              <FormControl>
                <Input placeholder="Inserisci il tuo nome" {...field} className="h-12 rounded-none border-t-0 border-l-0 border-r-0 border-b border-border bg-transparent px-0 transition-all focus:border-primary focus:ring-0 font-serif text-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserire un indirizzo email valido"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Indirizzo Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@esempio.it" {...field} className="h-12 rounded-none border-t-0 border-l-0 border-r-0 border-b border-border bg-transparent px-0 transition-all focus:border-primary focus:ring-0 font-serif text-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Recapito Telefonico</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 000 000 0000" {...field} className="h-12 rounded-none border-t-0 border-l-0 border-r-0 border-b border-border bg-transparent px-0 transition-all focus:border-primary focus:ring-0 font-serif text-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Oggetto</FormLabel>
              <FormControl>
                <Input placeholder="Area di interesse" {...field} className="h-12 rounded-none border-t-0 border-l-0 border-r-0 border-b border-border bg-transparent px-0 transition-all focus:border-primary focus:ring-0 font-serif text-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Il messaggio è obbligatorio",
      minLength: {
        value: 10,
        message: "Il messaggio deve contenere almeno 10 caratteri"
      },
      maxLength: {
        value: 2000,
        message: "Il messaggio non può superare i 2000 caratteri"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Messaggio</FormLabel>
            <FormControl>
              <Textarea placeholder="Descriva brevemente la sua richiesta..." {...field} className="min-h-[120px] rounded-none border-t-0 border-l-0 border-r-0 border-b border-border bg-transparent px-0 transition-all focus:border-primary focus:ring-0 font-serif text-lg resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none text-destructive text-sm font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-mono uppercase tracking-widest rounded-none bg-primary text-primary-foreground hover:bg-primary/90 shadow-none transition-all duration-300 relative overflow-hidden group">
        <span className="relative z-10 flex items-center justify-center gap-2">
          {isSubmitting ? <>
              <Loader2 className="animate-spin size-5" />
              Invio in corso...
            </> : <>
              Invia Richiesta
              <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
            </>}
        </span>
        {}
        <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent z-0" />
      </Button>
    </Form>;
}
export default function ContactForm() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return;
}